import PropTypes from 'prop-types';
import React, { Component } from 'react';
import $ from 'jquery';
import omit from 'lodash/omit';
import pickBy from 'lodash/pickBy';
import moment from 'moment';
import classnames from 'classnames';
import config from '../../config';
import Icon, { GLYPHS } from './icon';
import withDateFormat from '../auth/utils/withDateFormat';
import { getCurrentLanguageCode } from '../../modules/infra/i18n/language-selection-utils/utils';

class DatePicker extends Component {
  static parseDate(date) {
    return moment(date).format(config.dateFormatISO);
  }

  componentDidMount() {
    this.$input = $(this.input).datepicker({
      clearBtn: this.props.addClearBtn || false,
      autoclose: true,
      startDate: this.props.startDate || -Infinity,
      endDate: this.props.endDate || Infinity,
      datesDisabled: this.props.datesDisabled,
      daysOfWeekDisabled: this.props.daysOfWeekDisabled,
      beforeShowDay: date => classnames({
        'is-disabled': this.isDateMarkedDisabled(date),
        active: this.isDateActive(date),
      }),
      format: this.props.dateFormat.toLowerCase(),
      language: getCurrentLanguageCode(),
    });

    this.updateDateValue();
    this.$input.on('hide', this.onDateChange);

    // BootStrap date picker fires 'clearDate' on hiding if there is no clearBtn set,
    // thus preventing changing of dates
    if (this.props.addClearBtn) {
      this.$input.on('clearDate', () => this.props.onChange(''));
    }
  }

  componentDidUpdate() {
    this.$input.datepicker('setDatesDisabled', this.props.datesDisabled);
    this.$input.datepicker('setDaysOfWeekDisabled', this.props.daysOfWeekDisabled);
    this.updateDateValue();

    if (this.props.day) {
      this.$input.datepicker('setDate', moment(this.props.day).toDate());
    }
  }

  componentWillUnmount() {
    this.$input.datepicker('destroy');
  }

  onDateChange = () => {
    if (this.shouldPropagateChages()) this.propagateChanges();
  };

  updateDateValue() {
    if (this.props.range) {
      $(this.from).datepicker('setDate', this.props.from);
      $(this.to).datepicker('setDate', this.props.to);
    }
  }

  propagateChanges() {
    if (this.props.range) {
      const newFrom = DatePicker.parseDate($(this.from).datepicker('getDate'));
      const newTo = DatePicker.parseDate($(this.to).datepicker('getDate'));
      return this.props.onChange(newFrom, newTo);
    }

    return this.props.onChange(DatePicker.parseDate($(this.input).datepicker('getDate')));
  }

  shouldPropagateChages() {
    if (this.props.range) {
      const newFrom = $(this.from).datepicker('getFormattedDate');
      const newTo = $(this.to).datepicker('getFormattedDate');
      return newFrom !== this.props.from || newTo !== this.props.to;
    }

    return $(this.input).datepicker('getFormattedDate') !== this.props.value;
  }

  isDateMarkedDisabled(date) {
    const { datesMarkedDisabled, daysOfWeekMarkedDisabled } = this.props;
    const isDayMarked = item => item === moment(date).format(config.dateFormatISO);
    const isWeekDayMarked = item => item === (date.getDay() || 7);

    return datesMarkedDisabled.some(isDayMarked) || daysOfWeekMarkedDisabled.some(isWeekDayMarked);
  }

  isDateActive(date) {
    const { day } = this.props;
    return moment(date).format(config.dateFormatISO) === day;
  }

  _getDateValue() {
    if (this.props.range) {
      return '';
    }

    if (this.props.value) {
      return moment(this.props.value).format(this.props.dateFormat);
    }

    return this.props.defaultValue;
  }

  renderChildren() {
    if (!this.props.range) return null;
    return ([
      <input
        key="from"
        className="c-btn c-btn--small c-btn--blank u-width-85 u-ph0"
        type="text"
        readOnly
        name="start"
        value={this.props.from}
        ref={(el) => { this.from = el; }}
        disabled={this.props.disabled}
      />,
      <span className="u-align-middle" key="separator">
        &rarr;
      </span>,
      <input
        key="to"
        className="c-btn c-btn--small c-btn--blank u-width-85 u-ph0"
        type="text"
        readOnly
        name="end"
        value={this.props.to}
        ref={(el) => { this.to = el; }}
        disabled={this.props.disabled}
      />,
    ]);
  }

  render() {
    const date = this._getDateValue(); // eslint-disable-line no-underscore-dangle
    const isButton = this.props.component === 'button' || this.props.component === 'button-text';
    const HtmlTag = (() => {
      if (isButton) return 'button';
      if (this.props.range) return 'div';
      return 'input';
    })();
    const className = (() => {
      if (isButton) return 'c-btn c-btn--blank u-p--';
      if (this.props.range) return 'input-daterange';
      return 'c-input';
    })();
    const type = isButton ? 'button' : 'text';
    const children = (() => {
      if (this.props.component === 'button-text') return date;
      if (this.props.range) return this.renderChildren();
      if (!isButton) return null;
      return (<Icon glyph={GLYPHS.calendar} size={this.props.iconSize} />);
    })();

    // Omit props to prevent react warnings about unsupported html attributes
    // and to prevent overwriting properties we want to set
    const omitProps = [
      'component',
      'startDate',
      'endDate',
      'datesDisabled',
      'daysOfWeekDisabled',
      'daysOfWeekMarkedDisabled',
      'value',
      'datesMarkedDisabled',
      'day',
      'range',
      'from',
      'to',
      'iconSize',
    ];
    const props = { children, ...omit(pickBy(this.props), omitProps) };

    return (
      <HtmlTag
        className={className}
        type={type}
        ref={(el) => { this.input = el; }}
        value={date}
        data-automation-id={`${HtmlTag}:datePicker`}
        {...props}
      />
    );
  }
}
DatePicker.propTypes = {
  addClearBtn: PropTypes.bool,
  value: PropTypes.string,
  defaultValue: PropTypes.string,
  onChange: PropTypes.func,
  component: PropTypes.oneOf(['input', 'button', 'button-text']),
  startDate: PropTypes.oneOfType([PropTypes.string, PropTypes.instanceOf(Date)]),
  endDate: PropTypes.oneOfType([PropTypes.string, PropTypes.instanceOf(Date)]),
  datesDisabled:
    PropTypes.arrayOf(PropTypes.oneOfType([PropTypes.string, PropTypes.instanceOf(Date)])),
  daysOfWeekDisabled: PropTypes.arrayOf(PropTypes.number),
  daysOfWeekMarkedDisabled: PropTypes.arrayOf(PropTypes.number),
  datesMarkedDisabled:
    PropTypes.arrayOf(PropTypes.oneOfType([PropTypes.string, PropTypes.instanceOf(Date)])),
  day: PropTypes.oneOfType([PropTypes.string, PropTypes.instanceOf(Date)]),
  range: PropTypes.bool,
  from: PropTypes.string,
  to: PropTypes.string,
  disabled: PropTypes.bool,
  iconSize: PropTypes.oneOf(['', 'medium']),
  dateFormat: PropTypes.string.isRequired,
};
DatePicker.defaultProps = {
  addClearBtn: false,
  value: '',
  defaultValue: moment().format(config.dateFormat),
  onChange: () => null,
  component: 'input',
  startDate: null,
  endDate: null,
  datesDisabled: [],
  daysOfWeekDisabled: [],
  daysOfWeekMarkedDisabled: [],
  datesMarkedDisabled: [],
  day: '',
  range: false,
  from: '',
  to: '',
  disabled: false,
  iconSize: '',
};

export default withDateFormat()(DatePicker);
